import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";

interface Step {
  step: number;
  title?: string;
  description?: string;
}

interface HowItWorksProps {
  steps: Step[];
}

/**
 * HowItWorks
 *
 * Step-by-step guide with:
 * - Cards with step numbers
 * - Entrance animations on scroll
 * - Text from translations keyed by step number
 */
export function HowItWorks({ steps }: HowItWorksProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("howItWorks.title");
  const subtitle = t("howItWorks.subtitle");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="howItWorks" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">Process</SectionLabel>

          <h2 className="mb-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && <p className="text-base text-muted-foreground lg:text-lg">{subtitle}</p>}
        </div>

        {/* Steps */}
        <div className="relative mx-auto max-w-5xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <SpotlightCard
                key={step.step}
                className={cn(
                  "group",
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                )}
                style={{
                  transitionDelay: isVisible ? `${(index + 1) * 150}ms` : "0ms",
                }}
              >
                <SpotlightCardContent className="h-full p-6">
                  {/* Step number - rounded square */}
                  <div className="mb-6">
                    <div
                      className={cn(
                        "relative h-14 w-14 rounded-2xl",
                        "bg-muted",
                        "border border-primary/20",
                        "flex items-center justify-center",
                        "text-xl font-semibold text-primary",
                        "transition-all duration-300",
                        "group-hover:border-primary/40",
                      )}
                    >
                      {step.step}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="mb-3 text-lg font-semibold text-foreground transition-colors duration-300">
                    {t(`howItWorks.step${step.step}.title`, { defaultValue: step.title })}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {t(`howItWorks.step${step.step}.description`, {
                      defaultValue: step.description,
                    })}
                  </p>
                </SpotlightCardContent>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
